// https://fluentsite.z22.web.core.windows.net/quick-start
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
} from "@fluentui/react-components";
import { useEffect } from "react";
import { HashRouter as Router, useLocation } from "react-router-dom";
import { app } from "@microsoft/teams-js";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { TeamsFxContext } from "./components/Context";
import { IfsUserProvider } from "./context/IfsUserContext";
import config from "./components/sample/lib/config";
import { useTheme } from "./theme/OAS365Theme";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./css/notification.css";
import AppRoutes from "./routes/AppRoutes";
import { AppVersionProvider } from "./context/AppVersionProvider";

/** Renders AppRoutes when app is ready or when on Teams tab config page (so config shows in add-tab dialog, not main app). */
function RoutesGate({ loading }) {
  const location = useLocation();
  const isConfigPage = location.pathname === "config";
  const showRoutes = !loading || isConfigPage;
  return showRoutes ? <AppRoutes /> : null;
}

/**
 * Main app component: providers, router, and route tree.
 */
export default function App() {
  const { loading, theme, themeString, teamsUserCredential } =
    useTeamsUserCredential({
      initiateLoginEndpoint: config.initiateLoginEndpoint,
      clientId: config.clientId,
    });

  const currentTheme = useTheme();

  useEffect(() => {
    if (loading) {
      app.initialize().then(() => {
        app.notifySuccess();
      });
    }
  }, [loading]);

  return (
    <TeamsFxContext.Provider
      value={{ theme, themeString, teamsUserCredential }}
    >
      <FluentProvider
        theme={
          themeString === "dark"
            ? teamsDarkTheme
            : themeString === "contrast"
            ? teamsHighContrastTheme
            : {
                ...teamsLightTheme,
                backgroundColor: currentTheme.colors.backgroundColor,
              }
        }
        style={{ backgroundColor: "hsla(0, 0%, 0%, 0)" }}
      >
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={
            themeString === "dark" || themeString === "contrast"
              ? "dark"
              : "light"
          }
          closeButton={false}
        />

        <AppVersionProvider>
          <IfsUserProvider>
          <Router>
            <RoutesGate loading={loading} />
          </Router>
        </IfsUserProvider>
        </AppVersionProvider>
      </FluentProvider>
    </TeamsFxContext.Provider>
  );
}
